/**
 * Imports
 * @ignore
 * @internal
 */
import { useMemo, useEffect, useState, ReactElement } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import { Connected as Layout } from 'components/layout/default/'
import { useTheme, makeStyles } from '@material-ui/core/styles'
import useMediaQuery from '@material-ui/core/useMediaQuery'
import Grid from '@material-ui/core/Grid'
import { I18n } from '@aws-amplify/core'
import Filters from './../reporting/generic-filters'
import Heading from 'components/heading/'
import Box from '@material-ui/core/Box'
import Paper from '@material-ui/core/Paper'
import Typography from '@material-ui/core/Typography'
import gauges from 'application/gauges'
import { LOAD, getAggregateMeasurements, getDailyRainfall, getMetricSummaries } from 'actions/dashboard'
import * as Constants from 'application/constants'
import Gauge from './gauge'
import Link from '@material-ui/core/Link'
import diagramOverlay from './images/terrasense-metric-icons.png'
import WorkspaceIcon from './icons/workspace'
import MeetingRoomIcon from './icons/meeting-room'
import BatteryIcon from './icons/battery'
import NoConnectionIcon from './icons/no-connection'
import InfoOutlinedIcon from '@material-ui/icons/InfoOutlined'
import backgroundImage from './images/SoilSense-Dashboard.png'
import { useTemperature } from 'components/temperature-switch/temperatureContext'
import { TemperatureProvider } from 'components/temperature-switch/temperatureContext'
import { ConnectedDailyRainfall } from 'terrasense/components/reports/rainfall/DailyRainfall'
import color from 'color'
import { Dialog, IconButton } from '@material-ui/core'
import { State } from 'types/state'
import building from 'assets/icons/building'

/**
 * Types
 * @ignore
 * @internal
 */
interface ReadingProps {
  measurement: string
  value: number | null
}

/**
 * Localization
 * @ignore
 * @internal
 */
const I18N_NS = 'page.dashboard'
I18n.putVocabularies({
  en: {
    [`${I18N_NS}.title.surfaceTemp`]: 'Surface Temperature',
    [`${I18N_NS}.title.soilMoisture`]: 'Soil Moisture',
    [`${I18N_NS}.title.surfaceHumidity`]: 'Surface Humidity',
    [`${I18N_NS}.title.soilTemp`]: 'Soil Temperature',
    [`${I18N_NS}.title.rainfall`]: 'Rainfall',
    [`${I18N_NS}.title.windSpeed`]: 'Wind Speed',
    [`${I18N_NS}.title.tds`]: 'Total Dissolvable Solids',
    [`${I18N_NS}.title.ph`]: 'PH',
    [`${I18N_NS}.title.conductivity`]: 'Conductivity',

    [`${I18N_NS}.tooltip.co2`]: (
      <Typography paragraph={true}>
        {' '}
        <code>eCO2</code> – means estimated concentration of carbon dioxide calculated from known TVOC concentration.
      </Typography>
    ),

    [`${I18N_NS}.notice.temp.${Constants.STATUS.OK}`]: 'Comfortable',
    [`${I18N_NS}.notice.temp.${Constants.STATUS.WARNING}`]: 'Uncomfortable',
    [`${I18N_NS}.notice.temp.${Constants.STATUS.ERROR}`]: 'Uncomfortable',
    [`${I18N_NS}.notice.humidity.${Constants.STATUS.OK}`]: 'Comfortable',
    [`${I18N_NS}.notice.humidity.${Constants.STATUS.WARNING}`]: 'Uncomfortable',
    [`${I18N_NS}.notice.humidity.${Constants.STATUS.ERROR}`]: 'Uncomfortable',
    [`${I18N_NS}.notice.massPm10.${Constants.STATUS.OK}`]: 'Good',
    [`${I18N_NS}.notice.massPm10.${Constants.STATUS.CONSIDERABLE}`]: 'Elevated levels',
    [`${I18N_NS}.notice.massPm10.${Constants.STATUS.DANGEROUS}`]: 'Warning',
    [`${I18N_NS}.notice.massPm10.${Constants.STATUS.ERROR}`]: 'Dangerous',
    [`${I18N_NS}.notice.massPm25.${Constants.STATUS.OK}`]: 'Good',
    [`${I18N_NS}.notice.massPm25.${Constants.STATUS.CONSIDERABLE}`]: 'Elevated levels',
    [`${I18N_NS}.notice.massPm25.${Constants.STATUS.DANGEROUS}`]: 'Warning',
    [`${I18N_NS}.notice.massPm25.${Constants.STATUS.ERROR}`]: 'Dangerous',
  }
})

/**
 * Styles
 * @ignore
 * @internal
 */
const useStyles = makeStyles(() => ({
  // ── Layer 1: Full-screen background photo ──
  background: {
    backgroundImage: `url(${backgroundImage})`,
    backgroundSize: 'cover',
    backgroundPosition: 'center',
    backgroundAttachment: 'fixed',
    minHeight: '100%',
    width: '100%',
    position: 'fixed',
    top: 0,
    left: 0,
    zIndex: -1,
  },
  // ── Layer 2: Full-viewport canvas for all overlays ──
  // Fixed to the viewport so all children position relative to the screen.
  // pointerEvents: none so clicks pass through to UI beneath,
  // except where re-enabled on specific children (e.g. the chart).
  overlayCanvas: {
    position: 'fixed',
    top: 0,
    left: 0,
    width: '100vw',
    height: '100vh',
    pointerEvents: 'none',
    zIndex: 0,
  },
  // ── Diagram icons image ──
  // Positioned to match the reference: center-right of the screen.
  // Adjust top/left/width to taste using browser dev tools.
  diagramImage: {
    //position: 'fixed !important' as any,
    //top: '40% !important' as any,
    //left: '25%',
    width: '65vw',
    maxWidth: '950px',
    height: 'auto',
    opacity: 0.9,
  },
  rainfallChart: {
    position: 'fixed !important' as any,  // 👈 same here
    //top: '8%',
    //left: '38%',
    width: '45vw',
    maxWidth: '480px',
    pointerEvents: 'auto',
  },
  // ── Layer 3: Filter bar ──
  // Sits above the overlay canvas so it remains interactive.
  content: {
    position: 'relative',
    zIndex: 1,
  },
}))
//Fetch the daily rainfall data

/*useEffect(() => {
  void dispatch(getDailyTraffic(from, to, building, floor, area, workhours))
}, [dispatch, from, to, building, floor, area, workhours])*/
const Reading = ({ measurement, value }: ReadingProps): any => {
  const { convertTemperature, unit } = useTemperature()

  const isNoData = value === null || value === undefined || isNaN(Number(value))

  const displayValue = useMemo(() => {
    if (isNoData) return '-'
    if (measurement === 'soilTemp' || measurement === 'surfaceTemp') {
      return convertTemperature(value as number)
    }
    return value
  }, [measurement, value, convertTemperature, isNoData])

  const displayUnit = useMemo(() => {
    if (isNoData) return ''
    if (measurement === 'soilTemp' || measurement === 'surfaceTemp') {
      return '' // convertTemperature already appends unit if there is data
    }
    return gauges[measurement]?.unit ?? ''
  }, [measurement, unit, isNoData])

  return (
    <Box>{displayValue}{isNoData || measurement === 'soilTemp' || measurement === 'surfaceTemp' ? '' : ' '}{displayUnit}</Box>
  )
}

const TimeIndicator = (): ReactElement => {
  return <Typography>last 7 days</Typography>
}

const TerrasenseDashboard = (): ReactElement => {
  const classes = useStyles()
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'))

  const dispatch = useDispatch()
  const [building, setBuilding] = useState(null)
  const [floor, setFloor] = useState([])
  const [area, setArea] = useState([])
  const loading = useSelector((state: State) => state.loader[LOAD.GET_METRIC_SUMMARIES] !== false || state.loader[LOAD.GET_DAILY_RAINFALL] !== false)
  const debugMode = useSelector((state: State) => state._user.debugMode)
  const data = useSelector((state: State) => state.dashboard?.metricSummaries ?? {})
  const companyId = useSelector((state: State) => state._user.impersonate?.companyId ?? state.auth.companyId)

  const [isInitialLoad, setIsInitialLoad] = useState(true)

  useEffect(() => {
    if (!loading) {
      setIsInitialLoad(false)
    }
  }, [loading])

  useEffect(() => {
    //void dispatch(getAggregateMeasurements(building, floor, area, debugMode))
    void dispatch(getMetricSummaries(building, floor, area, companyId))
  }, [dispatch, building, floor, area, debugMode])
  useEffect(() => {
    void dispatch(getDailyRainfall(building, floor, area))
  }, [dispatch, building, floor, area])
  return (
    <TemperatureProvider>

      {/* ── Layer 1: Background photo ── */}
      {(!loading || !isInitialLoad) && <div className={classes.background} />}

      {/* ── Main Content Area ── */}
      <div className={classes.content}>
        <Layout
          useKey={false}
          progress={loading}
          disableFilter={true}
          hideToolbar={true}
          useCrumbTopBar={true}
          contain={true}
        >
          {/* Filter Bar */}
          <Grid container justify="center" style={{ position: 'relative', zIndex: 10 }}>
            <Grid item xs={12}>
              <Filters
                building={building}
                floor={floor}
                area={area}
                setBuilding={setBuilding}
                setFloor={setFloor}
                setArea={setArea}
                onDownload={null}
                downloadActive={false}
                downloading={false}
                initialFrom={null}
                initialTo={null}
                setDateRange={null}
                ActionComponent={<TimeIndicator />}
              />
            </Grid>
          </Grid>

          {/* Dashboard Visuals */}
          {(!loading || !isInitialLoad) && (
            <Grid
              container
              spacing={4}
              style={{
                marginTop: isMobile ? '20px' : '4vh',
                pointerEvents: 'none'
              }}>

              {/* Right-aligned Content Stack: Chart + Specs + Diagram */}
              <Grid item xs={12} style={{
                display: 'flex',
                flexDirection: 'column',
                justifyContent: isMobile ? 'center' : 'flex-end',
                alignItems: isMobile ? 'center' : 'flex-end',
                pointerEvents: 'auto',
                paddingRight: isMobile ? 0 : '5vw'
              }}>
                {/* 1. Chart & NPK */}
                <div style={{
                  width: '100%',
                  maxWidth: '850px',
                  zIndex: 2
                }}>
                  <ConnectedDailyRainfall />
                  <div style={{
                    color: '#FFF',
                    fontWeight: 'bold',
                    fontSize: 'clamp(0.85rem, 2vw, 1.5rem)',
                    marginTop: 'clamp(12px, 2vh, 24px)',
                    pointerEvents: 'none',
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: isMobile ? '2vw' : 'clamp(12px, 6vw, 84px)',
                    justifyContent: isMobile ? 'center' : 'flex-start',
                    flexWrap: 'wrap'
                  }}>
                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                      <span>PH = {loading || data.ph == null || isNaN(Number(data.ph)) ? '-' : data.ph.toFixed(1)}</span>
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                      <span>N = {loading || data.nitrogen == null || isNaN(Number(data.nitrogen)) ? '-' : `${data.nitrogen.toFixed(0)}ppm`}</span>
                      <Box fontSize={{ xs: 10, sm: 12, md: 14 }} fontWeight="normal" textAlign="center">Nitrogen</Box>
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                      <span>P = {loading || data.phosphorus == null || isNaN(Number(data.phosphorus)) ? '-' : `${data.phosphorus.toFixed(0)}ppm`}</span>
                      <Box fontSize={{ xs: 10, sm: 12, md: 14 }} fontWeight="normal" textAlign="center">Phosphorus</Box>
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                      <span>K = {loading || data.potassium == null || isNaN(Number(data.potassium)) ? '-' : `${data.potassium.toFixed(0)}ppm`}</span>
                      <Box fontSize={{ xs: 10, sm: 12, md: 14 }} fontWeight="normal" textAlign="center">Potassium</Box>
                    </div>
                  </div>
                </div>

                {/* 2. Diagram */}
                <div style={{
                  position: 'relative',
                  width: isMobile ? '90vw' : '100%',
                  maxWidth: '950px',
                  marginTop: isMobile ? '20px' : '80px',
                  pointerEvents: 'none',
                  zIndex: 1
                }}>
                  <img
                    src={diagramOverlay}
                    alt="Diagram Overlay"
                    className={classes.diagramImage}
                    style={{ width: '100%', maxWidth: 'none', position: 'relative', left: 'auto', top: 'auto', pointerEvents: 'none' }}
                  />
                  {/* ── EC (Conductivity) — above lightning bolt icon ── */}
                  <div style={{
                    position: "absolute",
                    bottom: "99%",
                    left: "9%",
                    color: "#FFFFFF",
                    fontWeight: "bold",
                    fontSize: 'clamp(0.8rem, 2vw, 1.6rem)',
                    pointerEvents: 'none',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                  }}>
                    {loading ? <Box>-</Box> : <Reading measurement="conductivity" value={data.conductivity} />}
                    <Box fontSize={{ xs: 10, sm: 12, md: 14 }} fontWeight="normal" textAlign="center">Conductivity</Box>
                  </div>

                  {/* ── Soil Moisture — above water drop icon ── */}
                  <div style={{
                    position: "absolute",
                    bottom: "91%",
                    left: "23%",
                    color: "#FFFFFF",
                    fontWeight: "bold",
                    fontSize: 'clamp(0.8rem, 2vw, 1.6rem)',
                    pointerEvents: 'none',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                  }}>
                    {loading ? <Box>-</Box> : <Reading measurement="soilMoisture" value={data.soilMoisture} />}
                    <Box fontSize={{ xs: 10, sm: 12, md: 14 }} fontWeight="normal" textAlign="center">Soil Moisture</Box>
                  </div>

                  {/* ── Surface Temperature — above thermometer icon ── */}
                  <div style={{
                    position: "absolute",
                    bottom: "83%",
                    left: "36%",
                    color: "#FFFFFF",
                    fontWeight: "bold",
                    fontSize: 'clamp(0.8rem, 2vw, 1.6rem)',
                    pointerEvents: 'none',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                  }}>
                    {loading ? <Box>-</Box> : <Reading measurement="surfaceTemp" value={data.surfaceTemp} />}
                    <Box fontSize={{ xs: 10, sm: 12, md: 14 }} fontWeight="normal" textAlign="center">Surface Temp</Box>
                  </div>

                  {/* ── Soil Temperature — above plant icon ── */}
                  <div style={{
                    position: "absolute",
                    bottom: "69%",
                    left: "56%",
                    color: "#FFFFFF",
                    fontWeight: "bold",
                    fontSize: 'clamp(0.8rem, 2vw, 1.6rem)',
                    pointerEvents: 'none',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                  }}>
                    {loading ? <Box>-</Box> : <Reading measurement="soilTemp" value={data.soilTemp} />}
                    <Box fontSize={{ xs: 10, sm: 12, md: 14 }} fontWeight="normal" textAlign="center">Soil Temp</Box>
                  </div>

                  {/* ── Rainfall — inside water drop icon ── */}
                  <div style={{
                    position: "absolute",
                    bottom: "35%",
                    left: "74%",
                    color: "#FFFFFF",
                    fontWeight: "bold",
                    pointerEvents: 'none',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                  }}>
                    <Box>{loading || data.rainfall == null || isNaN(Number(data.rainfall)) ? '-' : `${data.rainfall.toFixed(0)}mm`}</Box>
                    <Box fontSize={{ xs: 8, sm: 10, md: 12 }} fontWeight="normal" textAlign="center">Total Rainfall</Box>
                  </div>
                </div>
              </Grid>
            </Grid>
          )}
        </Layout>
      </div>

    </TemperatureProvider>
  )
}

export default TerrasenseDashboard